import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index.ts';
import type { SurfaceBrowseMode } from '../../types/index.ts';
import { branchRoutes } from '../../types/index.ts';
import { loadScenario } from '../actions/loadScenario.ts';

export type PlaybackMode = 'manual' | 'auto';

/**
 * Facet v2 information architecture — top-level screens.
 * 'landing'   — choose a source (upload / live bridge / bundled example)
 * 'portfolio' — the portfolio ledger (the suite run, flows × kinds × verdicts)
 * 'flow'      — one flow's ledger (scenarios grouped by kind)
 * 'scenario'  — the staged canvas (stage 1 verdict card → stage 2 story & evidence)
 * 'diff'      — scenario diff screen
 * 'impact'    — impact analysis screen
 */
export type AppView = 'landing' | 'portfolio' | 'flow' | 'scenario' | 'diff' | 'impact';

export interface UiState {
  /** v2 IA: which top-level screen is showing. */
  appView: AppView;
  /** v2 IA: flow whose ledger is open (view 'flow'); falls back to active flow. */
  viewFlowId: string | null;
  /** Staged canvas reading stage: 1 = verdict card, 2 = story & evidence. */
  scenarioStage: 1 | 2;
  selectedNode: string | null;
  hoveredNode: string | null;
  /** Node ID for the assertion tooltip — persists across node mouseleave
   *  so the tooltip stays visible while the mouse travels to it. */
  tooltipNodeId: string | null;
  playbackMode: PlaybackMode;
  playbackSpeed: number;
  showAssertionOverlay: boolean;
  expandedAssertionTypes: string[];
  assertionTierFilter: string[] | null;
  expandedPanels: string[];
  activePaths: string[];
  // Mobile UI state
  sidebarOpen: boolean;
  legendCollapsed: boolean;
  /** Run-story mini-legend on the canvas — expanded by default until dismissed. */
  runLegendCollapsed: boolean;
  // Catalog UI state
  catalogBrowseMode: SurfaceBrowseMode;
  selectedContextId: string | null;
  selectedSurfaceId: string | null;
  catalogSearchQuery: string;
  catalogProductFilter: string | null;
}

const initialState: UiState = {
  appView: 'landing',
  viewFlowId: null,
  scenarioStage: 1,
  selectedNode: null,
  hoveredNode: null,
  tooltipNodeId: null,
  playbackMode: 'manual',
  playbackSpeed: 1,
  showAssertionOverlay: false,
  expandedAssertionTypes: [],
  assertionTierFilter: null,
  expandedPanels: [],
  activePaths: [],
  sidebarOpen: false,
  legendCollapsed: true,
  runLegendCollapsed: false,
  catalogBrowseMode: 'context-first',
  selectedContextId: null,
  selectedSurfaceId: null,
  catalogSearchQuery: '',
  catalogProductFilter: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // ── v2 IA navigation ────────────────────────────────────────────────
    setAppView(state, action: PayloadAction<AppView>) {
      state.appView = action.payload;
    },
    openFlowLedger(state, action: PayloadAction<string>) {
      state.viewFlowId = action.payload;
      state.appView = 'flow';
    },
    /** Enter the staged canvas for a scenario — always lands on stage 1. */
    openScenarioView(state) {
      state.appView = 'scenario';
      state.scenarioStage = 1;
    },
    setScenarioStage(state, action: PayloadAction<1 | 2>) {
      state.scenarioStage = action.payload;
    },
    selectNode(state, action: PayloadAction<string | null>) {
      state.selectedNode = action.payload;
    },
    hoverNode(state, action: PayloadAction<string>) {
      state.hoveredNode = action.payload;
      state.tooltipNodeId = action.payload;
    },
    clearHover(state) {
      state.hoveredNode = null;
      // tooltipNodeId is NOT cleared here — it persists so the tooltip
      // stays visible while the mouse travels from node to tooltip.
    },
    clearTooltip(state) {
      state.tooltipNodeId = null;
    },
    setPlaybackMode(state, action: PayloadAction<PlaybackMode>) {
      state.playbackMode = action.payload;
    },
    setPlaybackSpeed(state, action: PayloadAction<number>) {
      state.playbackSpeed = action.payload;
    },
    toggleAssertionOverlay(state) {
      state.showAssertionOverlay = !state.showAssertionOverlay;
    },
    toggleAssertionTypeExpand(state, action: PayloadAction<string>) {
      const type = action.payload;
      const idx = state.expandedAssertionTypes.indexOf(type);
      if (idx >= 0) {
        state.expandedAssertionTypes.splice(idx, 1);
      } else {
        state.expandedAssertionTypes.push(type);
      }
    },
    toggleAssertionTierFilter(state, action: PayloadAction<string>) {
      const tier = action.payload;
      if (state.assertionTierFilter === null) {
        // Switching from "all" to "only this tier"
        state.assertionTierFilter = [tier];
      } else {
        const idx = state.assertionTierFilter.indexOf(tier);
        if (idx >= 0) {
          state.assertionTierFilter.splice(idx, 1);
          if (state.assertionTierFilter.length === 0) {
            state.assertionTierFilter = null; // back to "all"
          }
        } else {
          state.assertionTierFilter.push(tier);
        }
      }
    },
    clearAssertionTierFilter(state) {
      state.assertionTierFilter = null;
    },
    setExpandedPanels(state, action: PayloadAction<string[]>) {
      state.expandedPanels = action.payload;
    },
    togglePanel(state, action: PayloadAction<string>) {
      const panelId = action.payload;
      const idx = state.expandedPanels.indexOf(panelId);
      if (idx >= 0) {
        state.expandedPanels.splice(idx, 1);
      } else {
        state.expandedPanels.push(panelId);
      }
    },
    setActivePaths(state, action: PayloadAction<string[]>) {
      state.activePaths = action.payload;
    },
    togglePath(state, action: PayloadAction<string>) {
      const pathId = action.payload;
      const idx = state.activePaths.indexOf(pathId);
      if (idx >= 0) {
        state.activePaths.splice(idx, 1);
      } else {
        state.activePaths.push(pathId);
      }
    },
    // Mobile UI actions
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    closeSidebar(state) {
      state.sidebarOpen = false;
    },
    /** Idempotent open — used when a node is selected for its evidence. */
    openSidebar(state) {
      state.sidebarOpen = true;
    },
    toggleLegend(state) {
      state.legendCollapsed = !state.legendCollapsed;
    },
    toggleRunLegend(state) {
      state.runLegendCollapsed = !state.runLegendCollapsed;
    },
    // Catalog actions
    setCatalogBrowseMode(state, action: PayloadAction<SurfaceBrowseMode>) {
      state.catalogBrowseMode = action.payload;
    },
    selectContext(state, action: PayloadAction<string | null>) {
      state.selectedContextId = action.payload;
      state.selectedSurfaceId = null; // reset surface selection when context changes
    },
    selectSurface(state, action: PayloadAction<string | null>) {
      state.selectedSurfaceId = action.payload;
    },
    setCatalogSearchQuery(state, action: PayloadAction<string>) {
      state.catalogSearchQuery = action.payload;
    },
    setCatalogProductFilter(state, action: PayloadAction<string | null>) {
      state.catalogProductFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadScenario, (state, action) => {
      state.activePaths = branchRoutes(action.payload.seed.activeBranches);
      state.playbackMode = 'manual';
      state.selectedNode = null;
      state.hoveredNode = null;
      state.expandedAssertionTypes = [];
      state.assertionTierFilter = null;
    });
  },
});

export const {
  setAppView,
  openFlowLedger,
  openScenarioView,
  setScenarioStage,
  selectNode,
  hoverNode,
  clearHover,
  setPlaybackMode,
  setPlaybackSpeed,
  toggleAssertionOverlay,
  toggleAssertionTypeExpand,
  toggleAssertionTierFilter,
  clearAssertionTierFilter,
  setExpandedPanels,
  togglePanel,
  setActivePaths,
  togglePath,
  clearTooltip,
  toggleSidebar,
  closeSidebar,
  openSidebar,
  toggleLegend,
  toggleRunLegend,
  setCatalogBrowseMode,
  selectContext,
  selectSurface,
  setCatalogSearchQuery,
  setCatalogProductFilter,
} = uiSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectAppView = (state: RootState) => state.ui.appView;
export const selectViewFlowId = (state: RootState) => state.ui.viewFlowId;
export const selectScenarioStage = (state: RootState) => state.ui.scenarioStage;
export const selectSelectedNode = (state: RootState) => state.ui.selectedNode;
export const selectHoveredNode = (state: RootState) => state.ui.hoveredNode;
export const selectPlaybackMode = (state: RootState) => state.ui.playbackMode;
export const selectPlaybackSpeed = (state: RootState) => state.ui.playbackSpeed;
export const selectShowAssertionOverlay = (state: RootState) => state.ui.showAssertionOverlay;
export const selectExpandedAssertionTypes = (state: RootState) => state.ui.expandedAssertionTypes;
export const selectAssertionTierFilter = (state: RootState) => state.ui.assertionTierFilter;
export const selectExpandedPanels = (state: RootState) => state.ui.expandedPanels;
export const selectActivePaths = (state: RootState) => state.ui.activePaths;
export const selectTooltipNodeId = (state: RootState) => state.ui.tooltipNodeId;
export const selectSidebarOpen = (state: RootState) => state.ui.sidebarOpen;
export const selectLegendCollapsed = (state: RootState) => state.ui.legendCollapsed;
export const selectRunLegendCollapsed = (state: RootState) => state.ui.runLegendCollapsed;
export const selectCatalogBrowseMode = (state: RootState) => state.ui.catalogBrowseMode;
export const selectSelectedContextId = (state: RootState) => state.ui.selectedContextId;
export const selectSelectedSurfaceId = (state: RootState) => state.ui.selectedSurfaceId;
export const selectCatalogSearchQuery = (state: RootState) => state.ui.catalogSearchQuery;
export const selectCatalogProductFilter = (state: RootState) => state.ui.catalogProductFilter;

export default uiSlice.reducer;
